import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ALL_ELEMENTS } from "@/data";
import type {
	ForceElement,
	ForceList,
	ForceModel,
	ForceVehicle,
	ValidationMode,
	VehicleMountSelection,
} from "@/types/force";
import type { ElementType, FactionId, TrainingTier } from "@/types/game-data";
import { generateId } from "@/utils/ids";
import { calculateForceTotalCost } from "@/utils/points";

/** Maximum number of saved rosters */
const MAX_ROSTERS = 50;

interface ForceState {
	rosters: ForceList[];
	activeRosterId: string | null;
	validationMode: ValidationMode;

	// Roster management actions
	createRoster: (faction: FactionId, name: string, maxPoints?: number) => void;
	deleteRoster: (rosterId: string) => void;
	openRoster: (rosterId: string) => void;
	closeRoster: () => void;

	// Force-level actions (faction is locked at creation and cannot be changed)
	setForceName: (name: string) => void;
	setMaxPoints: (points: number) => void;
	resetForce: () => void;

	// Element actions
	addElement: (templateId: string, tier: TrainingTier) => void;
	removeElement: (elementId: string) => void;
	setElementTier: (elementId: string, tier: TrainingTier) => void;

	// Model actions
	addModel: (elementId: string, role?: "soldier" | "sergeant") => void;
	duplicateModel: (elementId: string, modelId: string, count?: number) => void;
	removeModel: (elementId: string, modelId: string) => void;
	setModelRole: (
		elementId: string,
		modelId: string,
		role: "commander" | "sergeant" | "soldier",
	) => void;
	setModelCustomName: (elementId: string, modelId: string, name: string) => void;

	// Loadout actions
	addWeapon: (elementId: string, modelId: string, weaponId: string) => void;
	removeWeapon: (elementId: string, modelId: string, weaponId: string) => void;
	addEquipment: (elementId: string, modelId: string, equipmentId: string) => void;
	removeEquipment: (elementId: string, modelId: string, equipmentId: string) => void;

	// Vehicle actions
	addVehicle: (chassisId: string) => void;
	removeVehicle: (vehicleId: string) => void;
	setVehicleMountWeapon: (vehicleId: string, slotId: string, weaponId: string | null) => void;

	// Validation
	toggleValidationMode: () => void;
}

function createEmptyForce(faction: FactionId = "taskforce", name = "New Force", maxPoints = 2000): ForceList {
	return {
		id: generateId("force"),
		name,
		faction,
		maxPoints,
		elements: [],
		vehicles: [],
		createdAt: Date.now(),
		updatedAt: Date.now(),
	};
}

function createModel(role: "commander" | "sergeant" | "soldier"): ForceModel {
	return {
		id: generateId("model"),
		role,
		weapons: [],
		equipment: [],
	};
}

/** Helper to update force and set updatedAt */
function touchForce(force: ForceList): ForceList {
	return { ...force, updatedAt: Date.now() };
}

/**
 * Helper to update the active roster within the rosters array.
 * Returns the updated rosters array with the active roster modified by the updater function.
 * If no active roster is found, returns the original array unchanged.
 */
function updateActiveRoster(
	rosters: ForceList[],
	activeRosterId: string | null,
	updater: (force: ForceList) => ForceList,
): ForceList[] {
	if (!activeRosterId) return rosters;
	return rosters.map((r) => (r.id === activeRosterId ? updater(r) : r));
}

export const useForceStore = create<ForceState>()(
	persist(
		(set) => ({
			rosters: [],
			activeRosterId: null,
			validationMode: "relaxed",

			// --------------------------------------------------
			// Roster management actions
			// --------------------------------------------------

			createRoster: (faction, name, maxPoints = 2000) =>
				set((state) => {
					if (state.rosters.length >= MAX_ROSTERS) return state;
					// Reject duplicate names (case-insensitive, trimmed)
					const trimmed = name.trim();
					if (!trimmed) return state;
					const isDuplicate = state.rosters.some(
						(r) => r.name.trim().toLowerCase() === trimmed.toLowerCase(),
					);
					if (isDuplicate) return state;
					const newForce = createEmptyForce(faction, trimmed, maxPoints);
					return {
						rosters: [...state.rosters, newForce],
						activeRosterId: newForce.id,
					};
				}),

			deleteRoster: (rosterId) =>
				set((state) => ({
					rosters: state.rosters.filter((r) => r.id !== rosterId),
					// If the deleted roster was active, go back to landing
					activeRosterId: state.activeRosterId === rosterId ? null : state.activeRosterId,
				})),

			openRoster: (rosterId) =>
				set((state) => {
					const exists = state.rosters.some((r) => r.id === rosterId);
					if (!exists) return state;
					return { activeRosterId: rosterId };
				}),

			closeRoster: () => set({ activeRosterId: null }),

			// --------------------------------------------------
			// Force-level actions (operate on active roster)
			// --------------------------------------------------

			setForceName: (name) =>
				set((state) => {
					const trimmed = name.trim();
					if (!trimmed) return state;
					// Reject duplicate names (case-insensitive) against other rosters
					const isDuplicate = state.rosters.some(
						(r) =>
							r.id !== state.activeRosterId &&
							r.name.trim().toLowerCase() === trimmed.toLowerCase(),
					);
					if (isDuplicate) return state;
					return {
						rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
							touchForce({ ...force, name: trimmed }),
						),
					};
				}),

			setMaxPoints: (maxPoints) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({ ...force, maxPoints }),
					),
				})),

			resetForce: () =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...createEmptyForce(force.faction, force.name, force.maxPoints),
							id: force.id,
							createdAt: force.createdAt,
						}),
					),
				})),

			// --------------------------------------------------
			// Element actions
			// --------------------------------------------------

			addElement: (templateId, tier) =>
				set((state) => {
					const template = ALL_ELEMENTS.find((t) => t.id === templateId);
					if (!template) return state;

					const element: ForceElement = {
						id: generateId("elem"),
						templateId,
						type: template.type as ElementType,
						tier,
						models: template.hasCommander ? [createModel("commander")] : [createModel("soldier")],
					};

					return {
						rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
							touchForce({
								...force,
								elements: [...force.elements, element],
							}),
						),
					};
				}),

			removeElement: (elementId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.filter((e) => e.id !== elementId),
						}),
					),
				})),

			setElementTier: (elementId, tier) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId ? { ...el, tier } : el,
							),
						}),
					),
				})),

			// --------------------------------------------------
			// Model actions
			// --------------------------------------------------

			addModel: (elementId, role = "soldier") =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId ? { ...el, models: [...el.models, createModel(role)] } : el,
							),
						}),
					),
				})),

			duplicateModel: (elementId, modelId, count = 1) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) => {
								if (el.id !== elementId) return el;
								const source = el.models.find((m) => m.id === modelId);
								if (!source) return el;
								// Respect max model count from template
								const template = ALL_ELEMENTS.find((t) => t.id === el.templateId);
								const maxModels = template?.maxModels ?? 999;
								const available = Math.max(0, maxModels - el.models.length);
								const toAdd = Math.min(count, available);
								if (toAdd <= 0) return el;
								const clones: ForceModel[] = Array.from({ length: toAdd }, () => ({
									id: generateId("model"),
									role: "soldier" as const,
									weapons: [...source.weapons],
									equipment: [...source.equipment],
								}));
								return { ...el, models: [...el.models, ...clones] };
							}),
						}),
					),
				})),

			removeModel: (elementId, modelId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.filter((m) => m.id !== modelId),
										}
									: el,
							),
						}),
					),
				})),

			setModelRole: (elementId, modelId, role) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) => (m.id === modelId ? { ...m, role } : m)),
										}
									: el,
							),
						}),
					),
				})),

			setModelCustomName: (elementId, modelId, name) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) =>
												m.id === modelId ? { ...m, customName: name || undefined } : m,
											),
										}
									: el,
							),
						}),
					),
				})),

			// --------------------------------------------------
			// Loadout actions
			// --------------------------------------------------

			addWeapon: (elementId, modelId, weaponId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) =>
												m.id === modelId ? { ...m, weapons: [...m.weapons, weaponId] } : m,
											),
										}
									: el,
							),
						}),
					),
				})),

			removeWeapon: (elementId, modelId, weaponId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) => {
												if (m.id !== modelId) return m;
												const idx = m.weapons.indexOf(weaponId);
												if (idx === -1) return m;
												const weapons = [...m.weapons];
												weapons.splice(idx, 1);
												return { ...m, weapons };
											}),
										}
									: el,
							),
						}),
					),
				})),

			addEquipment: (elementId, modelId, equipmentId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) =>
												m.id === modelId
													? {
															...m,
															equipment: [...m.equipment, equipmentId],
														}
													: m,
											),
										}
									: el,
							),
						}),
					),
				})),

			removeEquipment: (elementId, modelId, equipmentId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							elements: force.elements.map((el) =>
								el.id === elementId
									? {
											...el,
											models: el.models.map((m) => {
												if (m.id !== modelId) return m;
												const idx = m.equipment.indexOf(equipmentId);
												if (idx === -1) return m;
												const equipment = [...m.equipment];
												equipment.splice(idx, 1);
												return { ...m, equipment };
											}),
										}
									: el,
							),
						}),
					),
				})),

			// --------------------------------------------------
			// Vehicle actions
			// --------------------------------------------------

			addVehicle: (chassisId) =>
				set((state) => {
					const vehicle: ForceVehicle = {
						id: generateId("veh"),
						chassisId,
						mountSelections: [],
					};
					return {
						rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
							touchForce({
								...force,
								vehicles: [...force.vehicles, vehicle],
							}),
						),
					};
				}),

			removeVehicle: (vehicleId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							vehicles: force.vehicles.filter((v) => v.id !== vehicleId),
						}),
					),
				})),

			setVehicleMountWeapon: (vehicleId, slotId, weaponId) =>
				set((state) => ({
					rosters: updateActiveRoster(state.rosters, state.activeRosterId, (force) =>
						touchForce({
							...force,
							vehicles: force.vehicles.map((v) => {
								if (v.id !== vehicleId) return v;
								const existingIdx = v.mountSelections.findIndex((ms) => ms.slotId === slotId);
								const selection: VehicleMountSelection = { slotId, weaponId };
								const mountSelections = [...v.mountSelections];
								if (existingIdx >= 0) {
									mountSelections[existingIdx] = selection;
								} else {
									mountSelections.push(selection);
								}
								return { ...v, mountSelections };
							}),
						}),
					),
				})),

			// --------------------------------------------------
			// Validation
			// --------------------------------------------------

			toggleValidationMode: () =>
				set((state) => ({
					validationMode: state.validationMode === "strict" ? "relaxed" : "strict",
				})),
		}),
		{
			name: "so-force-builder-store",
			version: 2,
			migrate: (persisted, version) => {
				if (version === 1) {
					// v1 had { force: ForceList; validationMode: ValidationMode }
					const old = persisted as { force: ForceList; validationMode: ValidationMode };
					const hasContent =
						old.force.elements.length > 0 || old.force.vehicles.length > 0;
					return {
						rosters: hasContent ? [old.force] : [],
						activeRosterId: null,
						validationMode: old.validationMode,
					};
				}
				return persisted as ForceState;
			},
		},
	),
);

// --------------------------------------------------
// Selectors (used in components for derived data)
// --------------------------------------------------
// IMPORTANT: Zustand selectors must return stable references.
// Primitives (number, boolean, string) are safe because Object.is
// compares by value. But arrays/objects are compared by reference,
// so never return a new array/object from a selector — use useMemo
// in the component instead.

/** Select the currently active force, or null if on the landing page */
export const selectActiveForce = (state: ForceState): ForceList | null =>
	state.rosters.find((r) => r.id === state.activeRosterId) ?? null;

export const selectTotalCost = (state: ForceState) => {
	const force = selectActiveForce(state);
	return force ? calculateForceTotalCost(force) : 0;
};

export const selectIsOverBudget = (state: ForceState) => {
	const force = selectActiveForce(state);
	if (!force) return false;
	const total = calculateForceTotalCost(force);
	return force.maxPoints > 0 && total > force.maxPoints;
};
